import React, {useEffect, useMemo, useState} from 'react';
import clsx from 'clsx';
import {EditableAttribute} from '@netx/core-attribute/components/EditableAttribute';
import {ATTRIBUTE_SYSTEM_ID} from '@netx/core-attribute/constants';
import {useAttributes} from '@netx/core-attribute/hooks/useAttributes';
import {AttributeTemplateObjectType} from '@netx/core-attribute/schema';
import {SelectField} from '@netx/core-components/SelectField';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {enumToValues} from '@netx/core-next/utils/enum';
import {i18n} from '@netx/core/i18n';
import {SearchRuleWarningLabel, SearchSubTypeLabel} from '../../../../locales';
import type {CommonRuleEditorProps} from '../../../types';
import {MetadataNoValueTypes, MetadataType} from '../../constants';
import {getMetadataTypeLabel} from '../../utils';
import {
	getMetadataRuleEditorUtilityClass,
	METADATA_RULE_EDITOR_CLASS_NAME,
	metadataRuleEditorClasses,
	MetadataRuleEditorClasses,
} from './metadataRuleEditorClasses';

export type MetadataRuleEditorOwnerState = WithClasses<
	MetadataRuleEditorClasses,
	{}
>;

const useUtilityClasses = (ownerState: MetadataRuleEditorOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getMetadataRuleEditorUtilityClass, classes);
};

const MetadataRuleEditorRoot = styled('div', {
	name: METADATA_RULE_EDITOR_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	flex: '1 1 auto',
}));

const MetadataRuleEditorCriteria = styled('div', {
	name: METADATA_RULE_EDITOR_CLASS_NAME,
	slot: 'Criteria',
})(({theme}) => ({
	display: 'flex',
	flex: '0 0 auto',
	'& > * + *': {
		marginLeft: theme.spacing(1),
	},
}));

const MetadataRuleEditorValues = styled('div', {
	name: METADATA_RULE_EDITOR_CLASS_NAME,
	slot: 'Values',
})(({theme}) => ({
	display: 'flex',
	flex: '0 0 auto',
	marginTop: theme.spacing(1),
	'& > * + *': {
		marginLeft: `${theme.spacing(1)} !important`,
	},
}));

type Option = {
	label: string;
	value: string;
};

export type MetadataRuleEditorProps = WithClasses<
	MetadataRuleEditorClasses,
	Pick<CommonRuleEditorProps, 'validationErrors'> & {
		elementSubType1: MetadataType;
		elementSubType1Options?: MetadataType[];
		/** attribute value / range start */
		elementValue1: string | number | string[];
		elementValue1Unavailable: boolean;
		elementValue1Label: string;
		elementValue1Loading: boolean;
		/** attribute name */
		elementValue2: string;
		/** range end */
		elementValue3: string | number;
		elementValue2Unavailable?: boolean;
		onSubType1Change: (subType1: MetadataType) => void;
		onValue1Change: (value: string | number | string[]) => void;
		onValue2Change: (value: string) => void;
		onValue3Change: (value: string | number) => void;
	}
>;

const SEARCHABLE_SYSTEM_ATTRIBUTES = [
	ATTRIBUTE_SYSTEM_ID.assetId,
	ATTRIBUTE_SYSTEM_ID.file,
	ATTRIBUTE_SYSTEM_ID.name,
	ATTRIBUTE_SYSTEM_ID.checksum,
	ATTRIBUTE_SYSTEM_ID.identifiedPeople,
];

const MetadataRuleEditorInner = ({
	classes: _classes,
	className,
	elementSubType1,
	elementSubType1Options,
	elementValue1,
	elementValue1Unavailable,
	elementValue1Label,
	elementValue1Loading,
	elementValue2,
	elementValue3,
	elementValue2Unavailable,
	validationErrors,
	onSubType1Change,
	onValue1Change,
	onValue2Change,
	onValue3Change,
}: MetadataRuleEditorProps) => {
	const ownerState: MetadataRuleEditorOwnerState = {
		classes: _classes,
		className,
	};

	const [personName, setPersonName] = useState(elementValue1Label);

	useEffect(() => {
		setPersonName(elementValue1Label);
	}, [elementValue1Label]);

	const waitingPersonLabel = elementValue1Loading && personName === '';

	const classes = useUtilityClasses(ownerState);

	const {getAll, getLabelById, data} = useAttributes();

	const attributes = useMemo(
		() =>
			getAll({objectType: AttributeTemplateObjectType.ASSET}).filter(
				(attr) =>
					!attr.system ||
					SEARCHABLE_SYSTEM_ATTRIBUTES.includes(attr.attributeid),
			),
		[data],
	);

	const options = useMemo(
		() =>
			attributes
				.reduce<Option[]>((accum, attr) => {
					return [
						...accum,
						{
							label: getLabelById(attr.attributeid),
							value: attr.name,
						},
					];
				}, [])
				.sort((a, b) => a.label.localeCompare(b.label)),
		[attributes],
	);

	const selectedAttribute =
		!elementValue2Unavailable &&
		attributes.find((attr) => attr.name === elementValue2);

	const value = selectedAttribute
		? {
				label: getLabelById(selectedAttribute.attributeid),
				value: selectedAttribute.name,
			}
		: null;

	const _elementSubType1Options =
		elementSubType1Options || enumToValues(MetadataType);

	return (
		<MetadataRuleEditorRoot className={clsx(classes.root, className)}>
			<MetadataRuleEditorCriteria>
				<SelectField<Option, false, true, false>
					data-testid="metadata-rule-value-2"
					disableClearable
					error={validationErrors?.elementValue2?.message}
					label={i18n.t('i18n.selectAttribute')}
					name={'rule-value-2'}
					options={options}
					placeholder={
						elementValue2Unavailable
							? SearchRuleWarningLabel.DATA_UNAVAILABLE
							: undefined
					}
					value={value as Option}
					getOptionLabel={(option) => option.label}
					onChange={(event, option) => onValue2Change(option.value)}
					InputProps={{
						InputLabelProps: {
							shrink: elementValue2Unavailable || undefined,
						},
					}}
				/>

				{!elementValue2Unavailable ? (
					<SelectField<MetadataType, false, true, false>
						data-testid="metadata-rule-sub-type-1"
						disableClearable
						label={SearchSubTypeLabel.MATCH_TYPE}
						name={'rule-sub-type-1'}
						options={_elementSubType1Options}
						value={elementSubType1}
						getOptionLabel={getMetadataTypeLabel}
						onChange={(event, value) => onSubType1Change(value)}
					/>
				) : null}
			</MetadataRuleEditorCriteria>

			<MetadataRuleEditorValues>
				{selectedAttribute &&
				!(MetadataNoValueTypes as readonly number[]).includes(
					elementSubType1,
				) ? (
					<EditableAttribute
						data-testid="metadata-rule-value-1"
						attrData={{
							...selectedAttribute,
							mandatory: 1,
						}}
						error={validationErrors?.elementValue1?.message}
						label={
							elementSubType1 === MetadataType.MATCH_ANY_RANGE
								? i18n.t('i18n.from')
								: i18n.t('i18n.value')
						}
						loadingValue={elementValue1Loading}
						name={'rule-value-1'}
						placeholder={
							waitingPersonLabel
								? 'loading...'
								: elementValue1Unavailable
									? SearchRuleWarningLabel.DATA_UNAVAILABLE
									: undefined
						}
						value={
							selectedAttribute.attributeid === -122
								? elementValue1Unavailable || waitingPersonLabel
									? null
									: {id: elementValue1 as string, name: personName}
								: elementValue1 === ''
									? null
									: elementValue1
						}
						onValueChange={(value1) => {
							setPersonName((value1 as any)?.name ?? '');
							onValue1Change(
								value1 == null
									? ''
									: selectedAttribute.attributeid === -122
										? ((value1 as any)?.id ?? '')
										: (value1 as any),
							); // TODO: fix typing
						}}
					/>
				) : null}

				{selectedAttribute &&
				[MetadataType.MATCH_ANY_RANGE].includes(elementSubType1) ? (
					<EditableAttribute
						data-testid="metadata-rule-value-3"
						attrData={{
							...selectedAttribute,
							mandatory: 1,
						}}
						error={validationErrors?.elementValue3?.message}
						label={
							elementSubType1 === MetadataType.MATCH_ANY_RANGE
								? i18n.t('i18n.to')
								: i18n.t('i18n.value')
						}
						name={'rule-value-3'}
						value={elementValue3 === '' ? null : elementValue3}
						// @ts-ignore
						onValueChange={(value3: string | number) =>
							onValue3Change(value3 == null ? '' : value3)
						}
					/>
				) : null}
			</MetadataRuleEditorValues>
		</MetadataRuleEditorRoot>
	);
};

export const MetadataRuleEditorPrimitive = MetadataRuleEditorInner;

export const MetadataRuleEditor = MetadataRuleEditorPrimitive;

export default MetadataRuleEditor;

export type MetadataRuleEditorGenericProps = WithClasses<
	MetadataRuleEditorClasses,
	CommonRuleEditorProps & {}
>;

export const MetadataRuleEditorGenericPrimitive = ({
	elementValue2,
	...props
}: MetadataRuleEditorGenericProps) => {
	return (
		<MetadataRuleEditor {...props} elementValue2={elementValue2.toString()} />
	);
};

export const MetadataRuleEditorGeneric = MetadataRuleEditorGenericPrimitive;
